import Html from './html';
import VideoUrlModel from '../model/video_url';
import GQL from '../gql/gql';
import Cookies from './cookies';
import Banners from './banners';
import Reload from './reload';
import AppOptions from '../app/options';
import BannerZoneModel from '../model/banner_zone';

declare var videojs: any;
declare var google: any;

interface PlayOptions {
    clickedButton?: HTMLElement | null;
    autoplay?: boolean;
    mute?: boolean;
    setUrl?: boolean;
    focus?: boolean;
}

class Video {

    private players: Array<{selector: string, player: any, video: any}> = [];
    private reloadVideoBanners = false;

    private bgVideoTimer: ReturnType<typeof setTimeout>;

    protected imaOptions = {
        id: '',
        locale: '',
        debug: this.options.env == 'dev',
        autoPlayAdBreaks: true,
        disableAdControls: false,
        requestMode: 'onPlay',
        showCountdown: false,
        adsManagerLoadedCallback: (e: Event) => { }
    };

    constructor(
        private html: Html,
        private gql: GQL,
        private cookies: Cookies,
        private banners: Banners,
        private reload: Reload,
        private options: AppOptions
    ) { }

    async play(videoElement: string, video: any,  options: PlayOptions): Promise<void> {
        if (!video) {
            return;
        }

        if (!video.inBackground) {
            const isAdblockerActive = await this.hasAdblocker();

            if (isAdblockerActive) {
                video.Id = this.options.adblockerVideoId;
                video.Subtype = 'trailer';
            }
        }

        this.html.loadVideoElements()
            .then(() => this.gql.videoUrl(parseInt(video.Id, 10)))
            .then(videoUrl => {
                if (!videoUrl || !videoUrl.url) {
                    return Promise.reject('No video url');
                }

                this.initLanguage();
                this.stopPlaybacks(videoElement);
                this.setVideoData(videoElement, video);
                this.showVideoElement(videoElement);
               
                if (this.options.siteId == 1) {
                    this.setVideoData('#videoteca-video-info', video);
                }

                if (options.setUrl) {
                    this.setVideoUrl(video);
                }

                // Do not show banners if video is error video.
                if (videoUrl?.infoCode) {
                    video.Subtype = 'trailer';
                }

                return this.playUrl(videoElement, videoUrl, video, options);
            })
            .then(player => {
                this.players.push({selector: videoElement, player, video});
                this.setSharingButton(player, video, videoElement + ' .video-js');
            });

        if (options.clickedButton) {
            this.html.removeClass('.video__list-active', 'video__list-active');
            this.html.addClass(options.clickedButton, 'video__list-active');
        }
    }

    playBackground(videoElement: string, timeout: number, options: PlayOptions): void {
        if (this.bgVideoTimer) {
            clearTimeout(this.bgVideoTimer);
        }

        this.bgVideoTimer = setTimeout(() => {
            let video = this.html.getData(videoElement + ' video', 'video') as string | any;

            if (typeof video === "string" && video.length > 0) {
                video = JSON.parse(video);
            }

            if (video) {
                video.inBackground = true;
                this.html.show(videoElement);
                this.play(videoElement, video, {autoplay: options.autoplay, mute: true});
            }
        }, timeout);
    }

    videotecaLoadMore(selector: string, sectionId: number, btn: HTMLElement, url = '/video'): void {
        const page = this.html.getData(selector, 'pageNb');
        const nextPage = parseInt(page, 10) + 1;
        this.html.setData(selector, 'pageNb', nextPage);

        url += '?p=' + nextPage;

        if (sectionId) {
            url += '&s=' + sectionId;
        }

        this.html.fetchText(url)
            .then(html => {
                if (!html || html.length < 100) {
                    this.html.hide(btn);
                }
                this.html.appendHTML(selector, html)
            });
    }

    toggleMute(selector: string, forcedValue: boolean | null = null): void {
        const p = this.players.find((p: any) => {
            return p && p.player && p.selector !== selector;
        });

        if (!p || !p.player) {
            return;
        }

        if (forcedValue === null) {
            p.player.muted(!p.player.muted());
        } else {
            p.player.muted(forcedValue);
        }

        if (!p.player.muted() && p.player.volume() < 0.1) {
            p.player.volume(0.5);
        }

        if (p.player.muted()) {
            this.html.hide(selector + ' .icon.sound');
            this.html.show(selector + ' .icon.mute');
        } else {
            this.html.hide(selector + ' .icon.mute');
            this.html.show(selector + ' .icon.sound');
        }
    }

    closePip(): void {
        this.html.removeClass('.pip', 'pip');
    }

    private setVideoData(selector: string, video: any): void {
        this.html.writeHTML(selector + ' #video_title', video.Title);
        this.html.writeHTML(selector + ' #video_section', video.Section?.Title);
    }

    private showVideoElement(selector: string): void {
        this.html.hide(selector + ' .videoteca-thumb');
        this.html.show(selector + ' video');
    }

    private findButton(player: any, buttonType: string): any | null {
        const children = player.controlBar.children() as Array<any>;
        return children.find(c => c.options()?.type === buttonType);
    }

    private setSharingButton(player: any, video: any, selector: string): void {
        let shareButton = this.findButton(player, 'share-button');

        if (shareButton) {
            player.controlBar.removeChild(shareButton);
        }

        shareButton = player.controlBar.addChild('button', {
            type: 'share-button',
            className: 'vjs-icon-share',
        }, 12);

        shareButton?.on('click', () => {
            this.toggleShareHtml(player, video.Id, selector);
        });

        shareButton?.on('touchend', () => {
            this.toggleShareHtml(player, video.Id, selector);
        });

        player.on('userinactive', () => {
            this.hideShareHtml(selector);
        });
    }

    private hideShareHtml(selector: string): void {
        this.html.q(selector + ' .video-shares')?.remove();
    }

    private toggleShareHtml(player: any, videoId: number, selector: string): void {
        if (this.html.q(selector + ' .video-shares')) {
            this.hideShareHtml(selector);
            return;
        }

        this.html.fetchText('/video/' + videoId + '/share-html')
            .then(html => {
                this.html.appendHTML(selector, html);
            });
    }

    private setVideoUrl(video: any): void {
        let path = document.location.pathname;

        if (path.includes('.html')) {
            path = path.slice(0, path.lastIndexOf('/'));
        }

        path += video.Url + document.location.search;
        path = path.replace('//', '/');

        history.pushState(null, "video: " + video.Id, path);
    }

    private showBanners(video: any): boolean {
        if (!this.options.showAds) {
            return false;
        }

        return video.Subtype === 'trailer' ? false : true;
    }

    private playUrl(selector: string, videoUrl: VideoUrlModel | null, video: any, options: PlayOptions): Promise<any> {
        if (!videoUrl || !videojs) {
            return Promise.reject();
        }

        const player = videojs(selector + ' video', { language: this.options.country, restoreEl: true });

        if (!video.inBackground) {
            player.poster(video.Image.Src.replace('PLACEHOLDER', '1100x619'));
            player.titleBar.update({ title: video.Title });
        }

        let volume = this.getPlayerVolume();
        if (options.mute) {
            volume = 0;
            player.muted(true);
        }

        this.html.setData(selector, 'contentid', video.Id);
        player.hotkeysEnabled = true;

        if (this.showBanners(video)) {
            this.initIMA(player, volume);

            this.initVideoBanners()
                .then(vmap => {
                    player.ima.initializeAdDisplayContainer();
                    player.ima.setContentWithAdsResponse({
                        src: videoUrl.url, type: 'application/x-mpegURL'
                    }, vmap);
                });
        } else {
            player.src({
                src: videoUrl.url, type: 'application/x-mpegURL'
            });
        }

        player.ready(() => {
            if (video.Vtt) {
                this.setVttThumbnails(video.Vtt, player)
            }

            this.initPlayerEvents(player);
            this.setPlayerVolume(player, volume);

            player.on('keydown', (e:KeyboardEvent) => {
                if (player.hotkeysEnabled) {
                    this.handleHotkey(player, e);
                }
                e.stopPropagation();
                e.preventDefault();
            });

            if (options.focus) {
                player.tech({ IWillNotUseThisInPlugins: true }).el().focus();
            }

            if (options.autoplay) {
                player.play();
            }
        });

        return Promise.resolve(player);
    }

    private handleHotkey(player: any, e: KeyboardEvent): void {
        switch (e.code) {
        case 'Space':
            player.paused() ? player.play() :player.pause();
            break;
        case 'ArrowRight':
            player.currentTime(player.currentTime() + 5);
            break
        case 'ArrowLeft':
            player.currentTime(player.currentTime() - 5);
            break
        case 'KeyM':
            player.muted(!player.muted());
        }
    }

    stopPlaybacks(selector: string): void {
        this.players = this.players.filter((p: any) => {
            if (!p || !p.player) {
                return false;
            }

            if (p.selector !== selector) {
                try {
                    p.player.pause();
                    this.closePip();
                } catch (e) {console.log('stop playbacks', e)}
                return true;
            }

            p.player.dispose();
            p.player = null;
            p.video = null;
            return false;
        });
    }

    pausePlayback(selector: string): void {
        const p = this.players.find((p: any) => {
            return p && p.player && p.selector === selector
        });

        if (p && p.player) {
            p.player.pause();
        }
    }

    resumePlayback(selector: string): void {
        const p = this.players.find((p: any) => {
            return p && p.player && p.selector === selector
        });

        if (p && p.player) {
            p.player.play();
        }
    }

    private async initVideoBanners(): Promise<string> {
        if (this.reloadVideoBanners) {
            await this.banners.reloadVideoBanners();
        }

        const prerollBanner = await this.banners.getPreOrPostRoll('preroll');
        const postrollBanner = await this.banners.getPreOrPostRoll('postroll');

        let vmap = '<?xml version="1.0" encoding="UTF-8"?><vmap:VMAP xmlns:vmap="http://www.iab.net/videosuite/vmap" version="1.0">';

        if (prerollBanner) {
            // tslint:disable-next-line:max-line-length
            vmap += '<vmap:AdBreak timeOffset="start" breakType="linear" breakId="preroll"><vmap:AdSource id="preroll-ad-1" allowMultipleAds="false" followRedirects="true"><vmap:AdTagURI templateType="vast3"><![CDATA[' + prerollBanner.banner.vastUrl + ']]></vmap:AdTagURI></vmap:AdSource></vmap:AdBreak>';
        }

        if (postrollBanner) {
            // tslint:disable-next-line:max-line-length
            vmap += '<vmap:AdBreak timeOffset="end" breakType="linear" breakId="postroll"><vmap:AdSource id="postroll-ad-1" allowMultipleAds="false" followRedirects="true"><vmap:AdTagURI templateType="vast3"><![CDATA[' + postrollBanner.banner.vastUrl + ']]></vmap:AdTagURI></vmap:AdSource></vmap:AdBreak>';
        }

        vmap += '</vmap:VMAP>';

        this.reloadVideoBanners = true;

        return Promise.resolve(vmap);
    }

    private initIMA(player: any, volume: number): void {
        if (typeof player.ima !== 'function') {
            return;
        }

        let options = this.imaOptions;

        options.id = player.id();
        options.locale = this.options.country === 'si' ? 'sl' : this.options.country;

        options.adsManagerLoadedCallback = (e: Event) => {
            this.setAdPlayerVolume(player, player.ima.getAdsManager(), volume);
            player.ima.addEventListener('volumeChange', (e: Event) => this.onPlayerVolumeChange(player, e));

            player.ima.addEventListener(google.ima.AdEvent.Type.STARTED, () => {
                player.hotkeysEnabled = false;
            });

            player.ima.addEventListener(google.ima.AdEvent.Type.COMPLETE, () => {
                player.hotkeysEnabled = true;
            });
        };

        player.ima(options);
    }

    private setVttThumbnails(vttUrl: string, player: any): void {
        if (typeof player.vttThumbnails === 'function') {
            player.vttThumbnails({ src: vttUrl });
        } else if (player.vttThumbnails) {
            player.vttThumbnails.src(vttUrl);
        }
    }

    private initLanguage(): void {
        videojs.addLanguage('si', {
            'Play Video': 'Predvajaj',
            'Play': 'Predvajaj',
            'Pause': 'Pavza',
            'LIVE': 'V ŽIVO',
            'Mute': 'Tiho',
            'Unmute': 'Glasno',
            'Fullscreen': 'Celozaslonski način',
            'Picture-in-Picture': 'Slika v sliki',
            'Exit Picture-in-Picture': 'Izhod slika v sliki',
            'Non-Fullscreen': 'Izhod celozaslonski način'
        });
        videojs.addLanguage('hr', {
            'Play Video': 'Gledaj',
            'Play': 'Gledaj',
            'Pause': 'Pauza',
            'LIVE': 'Uživo',
            'Mute': 'Ugasi zvuk',
            'Unmute': 'Upali zvuk',
            'Fullscreen': 'Cijeli zaslon',
            'Picture-in-Picture': 'Slika u slici',
            'Exit Picture-in-Picture': 'Izlaz iz prikaza slike u slici',
            'Non-Fullscreen': 'Izlaz iz cijelog zaslona'
        });
    }

    private initPlayerEvents(player: any): void {
        player.bigPlayButton.on('touchend', (e: Event) => { this.onPlayerTouchEnd(player, e); });

        player.on('click',            (e: Event) => { this.onPlayerClick(player, e); });
        player.on('dblclick',         (e: Event) => { this.onPlayerDblClick(player, e); });
        player.on('start',            (e: Event) => { this.onPlayerStart(player, e); });
        player.on('ended',            (e: Event) => { this.onPlayerEnded(player, e); });
        player.on('timeupdate',       (e: Event) => { this.onPlayerTimeupdate(player, e); });
        player.on('pause',            (e: Event) => { this.onPlayerPause(player, e); });
        player.on('play',             (e: Event) => { this.onPlayerPlay(player, e); });
        player.on('loadedmetadata',   (e: Event) => { this.onPlayerLoadMetadata(player, e); });
        player.on('adstart',          (e: Event) => { this.onPlayerAdStart(player, e); });
        player.on('adend',            (e: Event) => { this.onPlayerAdEnd(player, e); });
        player.on('fullscreenchange', (e: Event) => { this.onPlayerFullscreenChange(player, e); });
        player.on('volumechange',     (e: Event) => { this.onPlayerVolumeChange(player, e); });
    }

    protected onPlayerDblClick(player: any, e: Event): void {
        if (player.isFullscreen()) {
            player.exitFullscreen();
        } else {
            player.requestFullscreen();
        }
    }

    protected onPlayerFullscreenChange(player: any, e: Event): void {
        player.nativeElement?.firstChild?.focus();
    }

    protected onPlayerVolumeChange(player: any, e: Event): void {
        const volume = player.muted() ? 0 : player.volume();
        const p = this.players.find(p => p.player === player);

        // toggling mute on/off for video in splash background
        // must not be saved and applied to all normal videos.
        // only save volume value if user changed it
        // for real video.
        if (p && p.video && !p.video.inBackground) {
            this.cookies.setIfImportantAllowed('playerVol', volume);
        }
    }

    protected onPlayerStart(player: any, e: Event): void {
        player.controlBar.el().style.visibility = 'visible';
    }

    protected onPlayerAdEnd(player: any, e: Event): void {
    }

    protected onPlayerAdStart(player: any, e: Event): void {
        this.banners
            .getPreOrPostRoll(player.ads.adType)
            .then((bannerZone) => {
                if (bannerZone) {
                    this.executeAppendScript(bannerZone);
                }
            })
    }

    protected onPlayerTouchEnd(player: any, e: Event): void {
        if (!player) {
            return;
        }
    }

    protected onPlayerClick(player: any, e: Event): void {
    }

    protected onPlayerPlay(player: any, e: Event): void {
        this.reload.stop();
    }

    protected onPlayerLoadMetadata(player: any, e: Event): void {
        if (!player) {
            return;
        }

        const p = this.players.find(p => p.player === player);

        if (!p || !p.player) {
            return;
        }

        var seekableRange = player.seekable();

        if (seekableRange && seekableRange.length > 0 && p.video.ObjectType === 'stream') {
            var dvrWindow = seekableRange.end(0) - seekableRange.start(0);
            if (dvrWindow > 300) {
                player.options_.liveui = true;
                player.controlBar.addClass('vjs-liveui');
                player.currentTime(player.seekable().end(0));
            }
        }
    }

    protected onPlayerPause(player: any, e: Event): void {
    }

    protected onPlayerTimeupdate(player: any, e: any): void {
    }

    protected onPlayerEnded(player: any, e: Event): void {
        const p = this.players.find(p => p.player === player);

        /**
         * If this player is playing in splsh background, we need to
         * hide it when it finishes - to show splash image.
         */
        if (p && p.player && p?.video.inBackground) {
            this.html.hide(p.selector);
        }
    }

    protected getPlayerVolume(): number {
        let volume = this.cookies.getFloat('playerVol', 0.5);
        return (volume === null) ? 0.5 : volume;
    }

    protected setPlayerVolume(player: any, volume: number): void {
        player.volume(volume);

        const volumeBarSelector = '#' + player.id() + ' .vjs-volume-level';
        this.html.setStyle(volumeBarSelector, 'width', (volume * 100) + '%')
    }

    protected setAdPlayerVolume(player: any, adsManager: any, volume: number): void {
        if (!adsManager) {
            return;
        }

        adsManager.setVolume(volume);

        const volumeBarSelector = '#' + player.id() + ' .ima-slider-level-div';
        this.html.setStyle(volumeBarSelector, 'width', (volume * 100) + '%')
    }

    protected executeAppendScript(bannerZone: BannerZoneModel): void {
        const bannerAppendUrl = bannerZone.banner.iframeAppendUrl;

        if (!bannerAppendUrl) {
            return;
        }

        const html = '<iframe src="' + bannerAppendUrl + '" alt="" style="width: 1px; height: 1px;" width="1" height="1"></iframe>';
        this.html.appendHTML('#banner_iframe_placeholder', html);
    }

    protected hasAdblocker(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const adblockEl = this.html.q('#adblock') as HTMLObjectElement;

            if (!adblockEl) {
                resolve(false);
            }

            adblockEl.onload = _ => { resolve(false); }
            adblockEl.onerror = _ => { resolve(true); }

            adblockEl.data = 'https://ads.api.24ur.si/adserver/adblock.xseki.dej.nehi';
        });
    }
}

export default Video;
